const MediaRecorderState = {
  NOT_INITIALIZED: 'notInitialized',
  RECORDING: 'recording',
  ERROR: 'error',
  INACTIVE: 'inactive',
};

class MediaRecorder {
  constructor() {
    this.isRecording = false;
    this.permissionsGranted = false;
    this.state = MediaRecorderState.NOT_INITIALIZED;
    this.recorder = null;
  }

  /**
   * Initialize the media recorder. This method should be called before any other methods are called.
   * This method will check if the platform has the necessary permissions to record audio. If the permissions are
   * granted, the state will be set to INACTIVE. If the permissions are not granted, the state will be set to ERROR to
   * prevent the app from crashing.
   * @returns {Promise<void>}
   */
  async init() {
    this.permissionsGranted = await this.checkForPermissions();
    this.state = this.permissionsGranted ? MediaRecorderState.INACTIVE : MediaRecorderState.ERROR;
  }

  /**
   * Check if the current platform has the necessary permissions to record audio. If not, the user will need to grant
   * permission before recording can start. This method will request the necessary permissions if they are not already
   * granted.
   * By default, this method will just return whatever value we have saved in the class property permissionsGranted.
   * The different MediaRecorders will have to override this method to implement the actual permissions check.
   * @returns {boolean} - True if the platform has the necessary permissions to record audio, false otherwise.
   */
  async checkForPermissions() {
    return this.permissionsGranted;
  }

  /**
   * Start recording audio.
   */
  async startRecording() {
    if (this.state === MediaRecorderState.NOT_INITIALIZED) {
      await this.init();
    }

    this.isRecording = true;
    this.state = MediaRecorderState.RECORDING;
  }

  /**
   * Stop recording audio.
   */
  stopRecording() {
    this.isRecording = false;
    this.state = MediaRecorderState.INACTIVE;
  }
}

export default MediaRecorder;
export { MediaRecorderState };
